import { DT_ROOM_FRAGMENT_LOADED_EVENT } from '../../../DtEventTypes';
import { flyToObliqueViewOfBox } from "../../../../lmv-compat/tools/FitToViewUtil";
import { FacetTypes } from "../../../facets/Facets";
import { PRIORITY } from "../../LabelCompositor";
import { HudLayer } from '../HudLayer';
import { HUD_LAYER } from '../HudLayerConstants';
import { SpaceLabelUI } from './SpaceLabel';
import { guessPlacement } from './SpacesPlacement';

import { THREE } from "@adsk/lmv";

const _tmpBox = new THREE.Box3();

/** Layer of see through labels, made to be seen top down, with a computed position */
export class SpacesLayer extends HudLayer {
    #cachedPlaceByEid;
    #labelByEid;
    #spaces;

    #waitingToLoad = new Map();
    #boundOnFragmentLoaded;

    constructor(facility, hud, viewer) {
        super(HUD_LAYER.SPACES.id, facility, hud, viewer);

        this.hasTrackingLabels = true;
        this.#cachedPlaceByEid = {};
        this.#labelByEid = new Map();
        this.#spaces = [];

        this.#boundOnFragmentLoaded = this.#onFragmentLoaded.bind(this);
    }

    clear() {
        this.#labelByEid.forEach((label) => label.dispose());
        this.#labelByEid.clear();
        this.#cachedPlaceByEid = {};
    }

    dispose() {
        this.clear();
    }

    init() {
        return this;
    }

    /** Returns "targetable" spaces when deferring asset labels. */
    getTargets() {
        if (!this.isOn()) return [];

        const spacesFacetDef = this.facility.facetsManager.getFacetDef(FacetTypes.spaces);
        return spacesFacetDef.filter.size ? spacesFacetDef.filter : this.#spaces.map(s => s.spaceFacetId);
    }

    compose(ctx, priority) {
        // Space labels are only composed at normal priority (for now?)
        if (priority !== PRIORITY.NORMAL) return;

        if (!this.#labelByEid.size) return;

        const deprioritizedUIs = [];

        // First pass to deal with deferred uis
        for (const ui of this.uis()) {
            // Uncollapse all UIs by default
            ui.setCollapsible(false);

            let target = ui.getCollisionTarget();
            if (!ctx.onScreen(target)) {
                // TODO: Could defer the label's "children" to global?
                ui.setVisible(false);
                continue;
            }

            // Deferral status will be used to determine if the label is collapsible
            // as well as the estimated size for collision
            const deferred = ctx.rollups.getDeferredForRoom(ui.data.node.id);
            ui.updateWithDeferred(deferred);

            // Check whether the available space is enough for a single line label
            ui.updateMaxWidth();

            if (!ui.hasEnoughSpace()) {
                ui.setCollapsible(true);
                target = ui.getCollisionTarget();
            }

            const hasCollisions = ctx.collisionTree.hasCollisions(target);
            if (hasCollisions) {
                // UI collides with previously placed uis, collapse it
                ui.setCollapsible(true);
                target = ui.getCollisionTarget();

                const collisions = ctx.collisionTree.getCollisions(target);
                for (let collision of collisions) {
                    // Already collapsed, or non-collapsible, skip
                    if (collision.ui.isCollapsed || !collision.ui.setCollapsible) continue;

                    // Collapse colliding UIs and reinsert them
                    ctx.collisionTree.remove(collision);
                    collision.ui.setCollapsible(true);
                    collision = collision.ui.getCollisionTarget();
                    ctx.collisionTree.insert(collision);
                }
            }

            if (!deferred.length && !ui.isSelected) {
                // Collect non-selected, non-deferred uis
                deprioritizedUIs.push(ui);
                continue;
            }

            // Forces space labels visible if they have any deferred assets details,
            // even if they may collide with one another
            ui.setVisible(true);
            ctx.place(target);
        }

        // Second pass to deal with non-selected, non-deferred uis
        for (const ui of deprioritizedUIs) {
            // There's still not enough space after the first pass, hide it
            if (!ui.hasEnoughSpace()) {
                ui.setVisible(false);
                continue;
            }

            const target = ui.getCollisionTarget();
            const hasCollisions = ctx.collisionTree.hasCollisions(target);
            if (hasCollisions) {
                // If there are still collisions after the first pass, hide it
                ui.setVisible(false);
            } else {
                // UI fits and there are no collisions, place it
                ui.setVisible(true);
                ctx.place(target);
            }
        }
    }

	onCameraChanged() {
		this.needsRedraw = true;
	}

    onHiddenChanged(/*hidden*/) {
        this.invalidate();
    }

    onFacetsUpdated(event) {
        this.#invalidateFilteredSpaces();
    }

    onModelChanged({ change }) {
        const { modelId, dbIds, needModelContentsReload } = change;

        if (needModelContentsReload) {
            return;
        }

        const changed = new Set(dbIds);
        for (const space of this.#spaces) {
            const { dbId, externalId, model } = space;
            if (model.urn() === modelId && changed.has(dbId)) {
                // Room name is the only relevant property that could change for the moment.
                this.#labelByEid.get(externalId)?.updateRoomName();
            }
        }
    }

    onClusterTransition(type, layerPrefs, isClustered) { /* Do nothing and allow labels. */ }

    async setVisible(visible) {

        const then = super.setVisible(visible);
        if (!visible) await then;

        //Hide or show room boundaries together with labels
        this.facility.facetsManager.facetsEffects?.setFloorplanEnabled(visible);

        this.invalidate();

        // Reinitialize rollups targets for this layer
        const spacesRollups = this.hud.layers.compositor.rollups.rollups.rooms;
        spacesRollups.clear();
        this.getTargets().forEach((id) => spacesRollups.set(id, {}));

        this.facility.facetsManager.highlightLocations();
        this.viewer.impl.invalidate(true);
        return then;
    }

    onDblClick(event) {
        //Double-click on one space will fit all currently filtered spaces.
        //This is a subjective choice of what would be more useful, it's easy
        //enough to change this to work on just the space that is target of the double-click.
        let box = new THREE.Box3();
        let box2 = new THREE.Box3();
        for (const roomInfo of this.#spaces) {
            const label = this.#labelByEid.get(roomInfo.externalId);
            if (label?.isSelected) {
                roomInfo.model.getElementBounds(roomInfo.dbId, box2);
                box.union(box2);
            }
        }

        flyToObliqueViewOfBox(this.facility.viewer, box, 1.0);

        event.preventDefault();
        event.stopPropagation();
    }

    update(ts) {
        if (!this.isVisible()) return;

        const toRemove = new Map(this.#labelByEid);
        for (const space of this.#spaces) {
            toRemove.delete(space.externalId);

            let label = this.#labelByEid.get(space.externalId);
            if (!label) {
                const placement = this.#cachedPlaceByEid[space.externalId];
                if (!placement || placement.needsToWait) continue;

                label = new SpaceLabelUI(this.hud, this, placement.defaultPosition, space);
                label.init(this.dom);
                this.#labelByEid.set(space.externalId, label);
            } else if (space.name !== label.text) {
                label.updateRoomName();
            }

            label.setSelected(space.selected, this.#spaces[0]?.selected);
        }

        for (const [key, label] of toRemove) {
            label.setVisible(false);
            label.dispose();
            this.#labelByEid.delete(key);
        }

        this.isDirty = false;

        // We always need to redraw because collision is handled during composition
        return true;
    }

    uis() {
        return this.#labelByEid.values();
    }

    #onFragmentLoaded(event) {
        const perModel = this.#waitingToLoad.get(event.model.id);
        if (!perModel) return;

        const space = perModel.get(event.fragId);
        if (!space) return;

        // Since the fragment is loaded, a valid placement is guaranteed
        this.#cachedPlaceByEid[space.externalId] = guessPlacement(this.viewer, space);
        this.invalidate();

        perModel.delete(event.fragId);
        if (perModel.size) return;

        this.#waitingToLoad.delete(event.model.id);
        if (this.#waitingToLoad.size) return;

        // All space fragments are loaded, remove listener
        this.viewer.removeEventListener(DT_ROOM_FRAGMENT_LOADED_EVENT, this.#boundOnFragmentLoaded);
    }

    #invalidateFilteredSpaces() {
        this.#spaces.length = 0;

        const spacesFacetDef = this.facility.facetsManager.getFacetDef(FacetTypes.spaces);
        const levelsFacetDef = this.facility.facetsManager.getFacetDef(FacetTypes.levels);
        const spacesFilter = spacesFacetDef.filter;
        const levelsFilter = levelsFacetDef.filter;

        if (!levelsFilter.size && !spacesFilter.size) {
            this.#invalidatePlacement();
            return;
        }

        //Get the list of spaces by listing them from the currently visible Space facet nodes
        const spaceNodes = this.facility.facetsManager.getFacet(FacetTypes.spaces);
        for (let node of spaceNodes) {

            if (node.isUnassignedId) continue;

            // Only collect rooms filtered by spaces or levels facet
            if (!spacesFilter.has(node.id) && !levelsFilter.has(getAssignedLevelId(node.roomSrcModel, node.dbId))) continue;

            if (!node.id.includes(":")) continue;

            // Get the bounds of the space for size estimation used to sort for placement priority
            node.roomSrcModel.getElementBounds(node.dbId, _tmpBox);

            this.#spaces.push({
                externalId: node.externalId,
                model: node.roomSrcModel,
                dbId: node.dbId,
                name: node.label,
                isMEPSpace: node.isMEPSpace,
                selected: node.selected,
                spaceFacetId: node.id,
                sizeEstimate: _tmpBox.min.distanceToSquared(_tmpBox.max)
            });
        }

        this.#spaces.sort(byVisibilityPriority);

        this.#invalidatePlacement();
    }

    #invalidatePlacement() {
        for (const space of this.#spaces) {
            if (this.#cachedPlaceByEid[space.externalId]) continue;

            const placement = guessPlacement(this.viewer, space);

            if (placement?.needsToWait) {
                if (this.#waitingToLoad.size === 0) {
                    this.viewer.addEventListener(DT_ROOM_FRAGMENT_LOADED_EVENT, this.#boundOnFragmentLoaded);
                }
                let perModel = this.#waitingToLoad.get(space.model.id);
                if (!perModel) {
                    perModel = new Map();
                    this.#waitingToLoad.set(space.model.id, perModel);
                }
                for (const fragId of placement.fragIds) {
                    perModel.set(fragId, space);
                }
            } else {
                this.#cachedPlaceByEid[space.externalId] = placement;
            }
        }

        this.invalidate();
    }
}

// In order of priority:
// 1. Selected spaces
// 2. MEP spaces
// 3. Larger spaces
function byVisibilityPriority(a, b) {
    if (a.selected !== b.selected) {
        return a.selected ? -1 : 1;
    }

    if (a.isMEPSpace !== b.isMEPSpace) {
        return a.isMEPSpace ? 1 : -1;
    }

    return b.sizeEstimate - a.sizeEstimate;
}

function getAssignedLevelId(model, dbId) {
    const dbId2levelId = model.getData().dbId2levelId;
    const levelDbId = dbId2levelId[dbId];
    if (levelDbId > 0) {
        return model.getInstanceTree().getNodeName(levelDbId, false);
    }
}
